package org.example.zone360.pages.coachDetails.sessionAllDetails

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.AnimationIterationCount
import com.varabyte.kobweb.compose.css.PointerEvents
import com.varabyte.kobweb.compose.css.TransformOrigin
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.animation
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.bottom
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.left
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.pointerEvents
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.modifiers.top
import com.varabyte.kobweb.compose.ui.modifiers.transform
import com.varabyte.kobweb.compose.ui.modifiers.transformOrigin
import com.varabyte.kobweb.compose.ui.modifiers.transition
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.theme.name
import kotlinx.browser.window
import org.example.zone360.theme.Palette
import org.jetbrains.compose.web.css.AnimationTimingFunction
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.ms
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.rgba
import org.jetbrains.compose.web.css.s
import org.w3c.dom.HTMLElement
import kotlin.math.max
import kotlin.random.Random


val WaveformStyle = CssStyle.base {
    Modifier
        .display(DisplayStyle.Flex)
        .gap(1.px)
        .height(90.percent)
        .backgroundColor(rgba(241, 245, 249, 0.5))
        .borderRadius(4.px)
}

val WaveBarStyle = CssStyle.base {
    Modifier
        .backgroundColor(rgba(148, 163, 184, 0.5))
        .width(2.px)
        .borderRadius(1.px)
        .transition(Transition.of("background-color", 300.ms))
}

val SegmentBoxStyle = CssStyle.base {
    Modifier
        .position(Position.Absolute)
        .top(0.px)
        .bottom(0.px)
        .backgroundColor(rgba(59, 130, 246, 0.5))
        .borderRadius(4.px)
}
val Dance = Keyframes {
    each(0.percent, 100.percent) {
        Modifier.transform { scaleY(0.5) }
    }
    50.percent {
        Modifier.transform { scaleY(1.0) }
    }
}


@Composable
fun AudioWaveform(
    modifier: Modifier = Modifier,
    progress: Float = 0f,
    audioDuration: Double = 0.0,
    isPlaying: Boolean = false,
    highlightSegments: List<Pair<Float, Float>> = emptyList(),
    currentKeywordSegmentIndex: Int = -1,
    onClick: (Double) -> Unit,
    breakpoint: Breakpoint
) {
    val clampedBarsCount = if (breakpoint < Breakpoint.MD) {
        100
    } else {
        when {
            audioDuration > 3600 -> 2000
            audioDuration > 1800 -> 1000
            audioDuration > 600  -> 500
            audioDuration > 300  -> 300
            else -> 100
        }
    }

    var waveformData by remember { mutableStateOf(generateWaveformData(clampedBarsCount)) }

    LaunchedEffect(clampedBarsCount) {
        waveformData = generateWaveformData(clampedBarsCount)
    }

    Box(
        modifier = modifier
            .position(Position.Relative) // 👈 Required for absolute children
            .then(WaveformStyle.toModifier()),
        contentAlignment = Alignment.Center
    ) {
        // 🔷 🔥 Highlighted Segment Boxes (added as you asked)
        highlightSegments.forEachIndexed { index, (start, end) ->
            val startFraction = start / audioDuration.toFloat()
            val endFraction = end / audioDuration.toFloat()
            val segmentWidth = (endFraction - startFraction).coerceAtLeast(0f)

            val isCurrentPlaying = (index == currentKeywordSegmentIndex)
            val shouldDrawBox = !(isPlaying && index == currentKeywordSegmentIndex)

            if (shouldDrawBox ) { // 👉 Don't draw highlight for currently playing segment
                Box(
                    modifier = SegmentBoxStyle.toModifier()
                        .position(Position.Absolute)
                        .left((startFraction * 100).percent)
                        .width((segmentWidth * 100).percent)
                        .height(100.percent)
                        .backgroundColor(Palette.Primary.rgbLight.copyf(alpha = 0.5f)) // Faint Highlight
                        .borderRadius(2.px)
                        .pointerEvents(PointerEvents.None)
                )
            }
        }

        val currentSegment = highlightSegments.getOrNull(currentKeywordSegmentIndex)?.let {
            it.first.toDouble() to it.second.toDouble()
        }

        val currentSegmentIndices: IntRange? = currentSegment?.let { (start, end) ->
            val startIndex = ((start / audioDuration) * clampedBarsCount).toInt()
            val endIndex = ((end / audioDuration) * clampedBarsCount).toInt()

            val range = startIndex..endIndex
            if (range.count() < 5) {
                val extraNeeded = 5 - range.count()
                val half = extraNeeded / 2
                val adjustedStart = (startIndex - half).coerceAtLeast(0)
                val adjustedEnd = (endIndex + (extraNeeded - half)).coerceAtMost(clampedBarsCount - 1)
                adjustedStart..adjustedEnd
            } else {
                range
            }
        }

        Row(
            modifier = Modifier.fillMaxWidth().height(100.percent)
                .onClick { event ->
                    val element = event.currentTarget?.unsafeCast<HTMLElement>()
                    val rect = element!!.getBoundingClientRect()
                    if (rect.width > 0) {
                        val clickX = (event.clientX - rect.left).coerceAtLeast(0.0)
                        val clickProgress = (clickX / rect.width).toFloat().coerceIn(0f, 1f)
                        val clickedTime = clickProgress * audioDuration
                        onClick(clickedTime)
                    }
                },
            horizontalArrangement = Arrangement.SpaceEvenly,
            verticalAlignment = Alignment.CenterVertically
        ) {
            waveformData.forEachIndexed { index, height ->
                val barStartTime = (index.toDouble() / clampedBarsCount) * audioDuration

                val isInAnySegment = highlightSegments.any { (start, end) ->
                    val extendedStart = (start - 0).coerceAtLeast(0f)
                    val extendedEnd = (end + 0).coerceAtMost(audioDuration.toFloat())
                    barStartTime in extendedStart.toDouble()..extendedEnd.toDouble()
                }

                val isFullSession = currentKeywordSegmentIndex == -1

                val isInCurrentSegment = currentSegmentIndices?.contains(index) ?: false

                val segmentProgressTime = currentSegment?.let { (start, end) ->
                    start + (progress * (end - start))
                } ?: (progress * audioDuration)

                val isPlayed = if (isFullSession) {
                    barStartTime <= (progress * audioDuration)
                } else {
                    isInCurrentSegment && barStartTime <= segmentProgressTime
                }

                val barColor = when {
                    isInCurrentSegment && isPlaying -> Palette.Primary.rgbLight
                    isPlayed -> Palette.Primary.rgbLight
//                    isInAnySegment -> Palette.Primary.rgbLight
                    else -> Palette.WaveBarColor.rgbLight.copyf(alpha = if (audioDuration > 3600 || audioDuration > 1800) 0.3f else 0.4f)
                }

                Box(
                    modifier = WaveBarStyle.toModifier()
                        .width(
                            when {
                                clampedBarsCount >= 2000 -> 0.2.px
                                clampedBarsCount >= 1000 -> 0.6.px
                                else -> 0.8.px
                            }
                        )
                        .height((height * 100).percent)
                        .backgroundColor(barColor)
                        .margin(leftRight =  if (isInCurrentSegment) 0.5.px else 0.px)
                        .then(
                            if (isInCurrentSegment && isPlaying) {
                                Modifier
                                    .animation(
                                        com.varabyte.kobweb.compose.css.CSSAnimation(
                                            name = Dance.name,
                                            duration = 1.5.s,
                                            delay = ((index % 50) * 120).ms,
                                            timingFunction = AnimationTimingFunction.EaseInOut,
                                            iterationCount = AnimationIterationCount.Infinite
                                        )
                                    )
                                    .transformOrigin(TransformOrigin.Center)
                            } else Modifier
                        )
//                        .onClick {
//                            val timestamp = (index.toDouble() / clampedBarsCount) * audioDuration
//                            onClick(timestamp)
//                        }
                )
            }
        }
    }
}




@Composable
fun SentenceAudioWaveform(
    modifier: Modifier = Modifier,
    progress: Float = 0f,
    startTime: Double,
    endTime: Double,
    barsCount: Int = 100,
    onProgressChange: (Float) -> Unit,
    isPlaying: Boolean = false,
) {
    var boxWidthPx by remember { mutableStateOf(0) }
    val duration = endTime - startTime
    val clampedBarsCount = max(1, barsCount) // Ensure at least one bar

    var waveformData by remember { mutableStateOf(generateWaveformData(clampedBarsCount)) }
    val waveformRef = remember { mutableStateOf<HTMLElement?>(null) }


    Box(
        modifier = modifier.then(WaveformStyle.toModifier())

            .onClick { event ->
                val element = event.currentTarget?.unsafeCast<HTMLElement>() ?: return@onClick
                val rect = element.getBoundingClientRect()

                if (rect.width > 0) {
                    val clickX = (event.clientX - rect.left).coerceAtLeast(0.0)
                    val newProgress = (clickX / rect.width).toFloat().coerceIn(0f, 1f)
                    onProgressChange(newProgress)
                }
            },
        contentAlignment = Alignment.Center
    ) {
        // Progress overlay
        Box(
            Modifier
                .position(Position.Absolute)
                .left(0.px)
                .top(0.px)
                .height(100.percent)
                .width((progress * 100).percent)
        )

        // Waveform bars
        Row(
            modifier = Modifier.fillMaxWidth().height(100.percent),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceEvenly
        ) {
            waveformData.forEachIndexed { index, height ->
                val isActive = index <= (progress * clampedBarsCount)

                Box(
                    modifier = WaveBarStyle.toModifier()
                        .height((height * 100).percent)
                        .backgroundColor(
                            if (isActive) Palette.Primary.rgbLight
                            else Palette.WaveBarColor.rgbLight
                        )
                )
            }
        }
    }
}


private fun generateWaveformData(bars: Int): List<Float> {
    // Generate random heights between 0.3 and 1.0 for more natural looking waveform
    return List(bars) { Random.nextFloat() * 0.7f + 0.3f }
}